import type { ExportEnvelope } from '@/lib/api';

/**
 * Write an export's two files to the reader's disk (EXIT-5, EXIT-6).
 *
 * **One response, two files, because that is what the spec delivers.** EXIT-5
 * says the manifest arrives "as CSV and JSON"; the API returns both renderings
 * of one walk, and this writes both from that one response — so the pair an
 * agency ends up holding describes a single instant. A UI that offered a format
 * picker would put the choice in front of a reader who wants both.
 *
 * **This is not the `<a href>` the folder view forbids.** That rule is about a
 * download SAS, which is a credential and must never sit in the DOM or in the
 * page's history (SEC-3, UPL-8). An object URL over bytes already in this tab's
 * memory is neither; there is nothing to leak that the caller does not already
 * hold.
 */

/** Kept short-lived: the object URL is revoked as soon as the click has been dispatched. */
function writeFile(name: string, blob: Blob): void {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = name;
  anchor.rel = 'noopener';
  anchor.click();
  URL.revokeObjectURL(href);
}

/** `bp-files-manifest-agency-smpsd-2026-08-07.csv` — sortable, and it says whose it is. */
function baseName(kind: string, envelope: ExportEnvelope): string {
  const day = envelope.generatedAt.slice(0, 10);
  return `bp-files-${kind}-${envelope.agencyId}-${day}`;
}

/**
 * @param kind `manifest` or `audit`, used only to name the files.
 */
export function saveExport(kind: string, envelope: ExportEnvelope): void {
  const name = baseName(kind, envelope);

  // **No BOM is added here.** The API already prefixes one so the same bytes
  // reach the agency however the file is written (Excel opens a BOM-less UTF-8
  // CSV in the system ANSI codepage). A second would render as `ï»¿` in the
  // first header cell.
  writeFile(`${name}.csv`, new Blob([envelope.csv], { type: 'text/csv;charset=utf-8' }));

  writeFile(
    `${name}.json`,
    // Indented, because the JSON rendering is the authoritative one (the CSV
    // cannot express `details`' structure) and a human reads it.
    new Blob([JSON.stringify(envelope, null, 2)], { type: 'application/json' }),
  );
}
